/**
 * Export quota / subscription gates.
 *
 * TODO(subscription): set SUBSCRIPTIONS_ENABLED = true when Pro billing ships.
 */
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getClientAuth, getClientDB } from '@canvas/firebase-config/client';
import { userPath } from '@canvas/shared-types';
import type { User, UserPlan } from '@canvas/shared-types';

/** Master switch — keep false so the editor is free for everyone. */
export const SUBSCRIPTIONS_ENABLED = false;

const LOCAL_KEY = 'craftara:exports-month';
const LEGACY_LOCAL_KEY = 'pixkit:exports-month';
export const DEFAULT_FREE_EXPORT_LIMIT = 5;

export interface ExportQuota {
  used: number;
  limit: number;
  remaining: number;
  unlimited: boolean;
  plan: UserPlan | 'guest';
  monthKey: string;
}

function path(segments: string) {
  return segments.replace(/^\/+/, '');
}

export function currentMonthKey(d = new Date()): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function readLocalUsage(monthKey: string): number {
  try {
    const raw = localStorage.getItem(LOCAL_KEY) || localStorage.getItem(LEGACY_LOCAL_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { monthKey?: string; count?: number };
    if (parsed.monthKey !== monthKey) return 0;
    return Math.max(0, Number(parsed.count) || 0);
  } catch {
    return 0;
  }
}

function writeLocalUsage(monthKey: string, count: number): void {
  localStorage.setItem(LOCAL_KEY, JSON.stringify({ monthKey, count }));
  localStorage.removeItem(LEGACY_LOCAL_KEY);
}

function planLimit(plan: UserPlan | undefined, featuresLimit?: number): number {
  if (!SUBSCRIPTIONS_ENABLED) return Number.POSITIVE_INFINITY;
  if (plan === 'pro' || plan === 'enterprise') return Number.POSITIVE_INFINITY;
  if (typeof featuresLimit === 'number' && featuresLimit > 0) return featuresLimit;
  return DEFAULT_FREE_EXPORT_LIMIT;
}

function toQuota(
  used: number,
  limit: number,
  plan: UserPlan | 'guest',
  monthKey: string
): ExportQuota {
  const unlimited = !Number.isFinite(limit);
  const remaining = unlimited ? Number.POSITIVE_INFINITY : Math.max(0, limit - used);
  return { used, limit: unlimited ? Infinity : limit, remaining, unlimited, plan, monthKey };
}

function freeUnlimitedQuota(monthKey = currentMonthKey()): ExportQuota {
  return toQuota(0, Number.POSITIVE_INFINITY, 'pro', monthKey);
}

/**
 * Read export quota for the signed-in user (or guest localStorage fallback).
 * When subscriptions are off, always returns unlimited.
 */
export async function fetchExportQuota(): Promise<ExportQuota> {
  const monthKey = currentMonthKey();

  // Free for now — skip Firestore / local counters
  if (!SUBSCRIPTIONS_ENABLED) {
    return freeUnlimitedQuota(monthKey);
  }

  const auth = getClientAuth();
  const uid = auth.currentUser?.uid;

  if (!uid) {
    const used = readLocalUsage(monthKey);
    return toQuota(used, DEFAULT_FREE_EXPORT_LIMIT, 'guest', monthKey);
  }

  try {
    const db = getClientDB();
    const ref = doc(db, path(userPath(uid)));
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      return toQuota(0, DEFAULT_FREE_EXPORT_LIMIT, 'free', monthKey);
    }
    const data = snap.data() as Partial<User> & { exportsMonthKey?: string };
    const plan = (data.plan || 'free') as UserPlan;
    const limit = planLimit(plan, data.planInfo?.features?.maxExportsPerMonth);
    const used =
      data.exportsMonthKey === monthKey ? Number(data.exportsThisMonth) || 0 : 0;
    return toQuota(used, limit, plan, monthKey);
  } catch (err) {
    console.warn('Failed to load export quota; using local counter', err);
    const used = readLocalUsage(monthKey);
    return toQuota(used, DEFAULT_FREE_EXPORT_LIMIT, 'guest', monthKey);
  }
}

export function canExportNow(quota: ExportQuota): { ok: boolean; reason?: string } {
  if (!SUBSCRIPTIONS_ENABLED) return { ok: true };
  if (quota.unlimited || quota.remaining > 0) return { ok: true };
  return {
    ok: false,
    reason: `Free plan limit reached (${quota.limit}/month). Upgrade to Pro for unlimited exports.`,
  };
}

/**
 * Increment export counters after a successful download.
 * No-op while subscriptions are disabled.
 */
export async function recordExportSuccess(): Promise<ExportQuota> {
  const monthKey = currentMonthKey();

  if (!SUBSCRIPTIONS_ENABLED) {
    return freeUnlimitedQuota(monthKey);
  }

  const auth = getClientAuth();
  const uid = auth.currentUser?.uid;

  if (!uid) {
    const used = readLocalUsage(monthKey) + 1;
    writeLocalUsage(monthKey, used);
    return toQuota(used, DEFAULT_FREE_EXPORT_LIMIT, 'guest', monthKey);
  }

  try {
    const db = getClientDB();
    const ref = doc(db, path(userPath(uid)));
    const snap = await getDoc(ref);
    const data = (snap.exists() ? snap.data() : {}) as Partial<User> & {
      exportsMonthKey?: string;
    };
    const plan = (data.plan || 'free') as UserPlan;
    const limit = planLimit(plan, data.planInfo?.features?.maxExportsPerMonth);
    const prev =
      data.exportsMonthKey === monthKey ? Number(data.exportsThisMonth) || 0 : 0;
    const used = prev + 1;

    await setDoc(
      ref,
      {
        exportsThisMonth: used,
        exportsMonthKey: monthKey,
        totalExports: (Number(data.totalExports) || 0) + 1,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return toQuota(used, limit, plan, monthKey);
  } catch (err) {
    console.warn('Failed to persist export count; using local counter', err);
    const used = readLocalUsage(monthKey) + 1;
    writeLocalUsage(monthKey, used);
    return toQuota(used, DEFAULT_FREE_EXPORT_LIMIT, 'guest', monthKey);
  }
}
